import { Stack, Typography } from '@mui/material';
import {
  GetReportsQueryFilters,
  Report,
  REPORTS_QUERY_FILTER_KEYS,
  useGetReportsQuery,
} from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { pickOnlyParamsFromFilterKeys, toggleMultipleValueArrayProperty } from '../utils';
import { ReportSelectRow } from './ReportSelectRow';
import { ReportTagSelect, ReportTagSelectProps } from './ReportTagSelect';

type ReportExpandedSelectProps = PropsWithChildren<{
  schoolId: string;
  selectedIds: string[];
  onApply: (v: string[]) => void;
  onClear: () => void;
  onClose: () => void;
  filters?: GetReportsQueryFilters;
}>;
export const ReportExpandedSelect: FC<ReportExpandedSelectProps> = ({
  schoolId,
  selectedIds: initialSelectedIds,
  onApply,
  onClose,
  onClear,
  filters = {},
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

  const { data, hasNextPage, isLoading, isFetchingNextPage, fetchNextPage, params, setParams } =
    useGetReportsQuery(
      {
        school_id: schoolId,
        filters: pickOnlyParamsFromFilterKeys(REPORTS_QUERY_FILTER_KEYS, filters),
      },
      {
        refetchOnMount: 'always',
      },
    );

  const toggleReportIds = useCallback((v: string[]) => {
    setSelectedIds((ids) => toggleMultipleValueArrayProperty(ids, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedIds);
    onClose();
  }, [selectedIds, onApply, onClose]);

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const entries =
      data.pages.reduce((prev, curr) => [...prev, ...curr.results], [] as Report[]) ?? [];

    if (!entries.length)
      return (
        <Stack m={1} gap={2}>
          <Typography>
            <FormattedMessage id="input-NoOptionsFound" />
          </Typography>
          <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
        </Stack>
      );

    return (
      <Stack mb={2} gap={1}>
        {entries.map((report) => (
          <ReportSelectRow
            onClick={(id) => {
              toggleReportIds([id]);
              handleChangeQuery('');
            }}
            key={report.id}
            report={report}
            isSelected={selectedIds.includes(report.id)}
          />
        ))}
        {hasNextPage && <SelectContentSkeleton />}
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [data, handleChangeQuery, toggleReportIds, selectedIds, handleApply, onClear, hasNextPage]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedIds.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
      isFetchingNextPage={isLoading || isFetchingNextPage}
      hasNextPage={hasNextPage}
      onClickInputArea={() => inputRef.current?.focus()}
      onFetchNextPage={fetchNextPage}
      showNextPageLoading={false}
    >
      {renderReportTags({
        ids: selectedIds,
        tagProps: { size: 'small' },
        onDelete: (id) => toggleReportIds([id]),
      })}
      <SelectSearchInput
        ref={inputRef}
        autoFocus
        value={params.query || ''}
        onChangeText={handleChangeQuery}
      />
    </ExpandedSelect>
  );
};

type RenderReportTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps?: Omit<ReportTagSelectProps, 'id'> | ((v: string) => Omit<ReportTagSelectProps, 'id'>);
};

export const renderReportTags = ({ ids, onDelete, onClick, tagProps }: RenderReportTagsParams) =>
  ids.map((v) => (
    <ReportTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
